import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import styles from './MostUsedWorkflow.module.scss';
import { useFetch } from 'hooks/useFetch';
import { dashboardEndpoints } from 'hooks/endpoints';
import SvgWithBranding from 'component/hoc/branding/withBrandingColor.hoc';
import Tooltip from 'component/Custom/tooltip/Tooltip';
import Modal from 'component/hoc/modal/Modal';
import TableComponent from 'component/molecules/Table/TableComponent';
import NoData from 'component/Custom/NoData/NoData';

const MESSAGES_COLORS = ['#0044FF', '#0236C6', '#3388FF', '#0066FF', '#55AAFF'];

const MostUsedWorkflow = ({ id, startDate, endDate }) => {
  const chartRef = useRef(null);
  const [series, setSeries] = useState([{ name: 'Messages', data: [] }]);
  const [categories, setCategories] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState({});

  const { data, refetch } = useFetch(
    ['topWorkflows', startDate, endDate, id],
    dashboardEndpoints.getTopWorkflows(startDate, endDate, id),
    { enabled: false },
    id ? { botId: id } : {}
  );

  const {
    data: questionsByWorkflow,
    refetch: refetchQuestionsByWorkflow,
    isLoading,
  } = useFetch(
    [
      'questions-by-workflow',
      startDate,
      endDate,
      id,
      selectedWorkflow?.workflowId,
    ],
    dashboardEndpoints.getQuestionsByWorkflow(startDate, endDate),
    { enabled: false },
    {
      ...(id && { botId: id }),
      ...(selectedWorkflow?.workflowId && {
        WorkflowId: selectedWorkflow.workflowId,
      }),
    }
  );

  useEffect(() => {
    if (selectedWorkflow) {
      refetchQuestionsByWorkflow();
    }
  }, [selectedWorkflow]);

  useEffect(() => {
    refetch();
  }, [startDate, endDate]);

  useEffect(() => {
    const rows = data?.data || [];
    const cats = rows.map((r) => r.workflowName);
    const vals = rows.map((r) => r.nodeCount ?? 0);
    setCategories(cats);
    setSeries([{ name: 'Usage Count', data: vals }]);
  }, [data]);

  const BAR_HEIGHT = 70;
  const BAR_GAP = 18;
  const MIN_HEIGHT = 470;
  const dynamicHeight = useMemo(
    () =>
      Math.max(
        MIN_HEIGHT,
        (categories?.length || 0) * (BAR_HEIGHT + BAR_GAP) + 16
      ),
    [categories]
  );

  const valueAnnotations = useMemo(() => {
    if (!series[0]?.data?.length) return [];
    const isSingle = (series[0]?.data?.length || 0) === 1;

    return series[0].data.map((val, i) => ({
      x: val,
      y: categories[i],
      marker: {
        size: 0,
        strokeWidth: 0,
        fillColor: 'transparent',
        strokeColor: 'transparent',
      },
      label: {
        text: String(val),
        style: {
          background: 'transparent',
          color: '#000',
          fontSize: '14px',
          fontWeight: 600,
        },
        borderColor: 'transparent',
        borderWidth: 0,
        offsetX: 18,
        offsetY: isSingle ? -120 : 40,
      },
    }));
  }, [series, categories]);

  const chartSettings = useMemo(
    () => ({
      series,
      options: {
        chart: {
          id: 'messages-by-topic-chart',
          type: 'bar',
          height: dynamicHeight,
          background: '#FFFFFF',
          toolbar: { show: false },
          animations: { enabled: true },
          events: {
            dataPointSelection: (_event, _chartContext, config) => {
              const idx = config?.dataPointIndex ?? -1;
              const rows = data?.data || [];
              const selected = rows[idx];
              if (selected) {
                setSelectedWorkflow(selected);
                setShowModal(true);
              }
            },
          },
        },
        plotOptions: {
          bar: {
            horizontal: true,
            barHeight: `${BAR_HEIGHT}px`,
            borderRadius: 8,
            distributed: true,
            dataLabels: {
              position: 'bottom',
            },
          },
        },
        colors: MESSAGES_COLORS,
        dataLabels: {
          enabled: true,
          textAnchor: 'center',
          offsetY: 0,
          offsetX: 20,
          style: {
            fontSize: '14px',
            fontFamily: 'Popins; sans-serif',
            fontWeight: 500,
            colors: ['#fff'],
          },
          formatter: (val, opts) =>
            opts.w.globals.labels[opts.dataPointIndex] || '',
        },
        xaxis: {
          categories,
          labels: { show: false },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: {
          show: false,
          labels: { show: false },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        grid: {
          show: false,
          padding: { top: 8, right: 48, bottom: 8, left: 0 },
        },
        tooltip: {
          enabled: true,
          shared: false,
          intersect: true,
          custom: function ({ series, seriesIndex, dataPointIndex, w }) {
            const label = w.globals.labels?.[dataPointIndex] ?? '';
            const value = series?.[seriesIndex]?.[dataPointIndex] ?? 0;
            return `
              <div style="
                padding:6px 8px;
                background:#111827;
                color:#fff;
                border-radius:6px;
                font-size:12px;
                line-height:1.3;
                box-shadow:0 2px 8px rgba(0,0,0,0.15);
              ">
                <div style="font-weight:600;margin-bottom:2px;">${label}</div>
                <div>${value}</div>
              </div>
            `;
          },
        },
        legend: { show: false },
        annotations: {
          position: 'front',
          points: valueAnnotations,
        },
      },
    }),
    [series, categories, valueAnnotations, dynamicHeight]
  );

  const getRows = () => {
    const vals = series?.[0]?.data || [];
    const n = Math.min(categories.length, vals.length);
    const rows = [];
    for (let i = 0; i < n; i += 1) {
      rows.push({ workflow: categories[i] ?? '', usage: Number(vals[i] ?? 0) });
    }
    return rows;
  };

  const downloadCSV = () => {
    const rows = getRows();
    const header = ['Workflow', 'Usage Count'];
    const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines = [header.join(',')].concat(
      rows.map((r) => `${escape(r.workflow)},${r.usage}`)
    );
    const csv = lines.join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'most_used_workflow.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownload = () => {
    downloadCSV();
  };

  return (
    <div ref={chartRef} className={styles['chart-container']}>
      <div className={styles['actions-container']}>
        <div className={styles['title-container']}>
          <span className={styles['title']}>Most used workflow</span>
          <Tooltip
            className={styles['tooltip']}
            content='Shows which workflows were used most often and
how many times during the selected time frame.'
          >
            <SvgWithBranding type='info' color='#0744E1' width={22} />
          </Tooltip>
        </div>
        {series[0]?.data.length === 0 ? (
          <Tooltip content='No data available to export'>
            <SvgWithBranding
              className={`${styles['download-icon']} ${styles['disabled']}`}
              type='download2'
              onClick={() => {}}
            />
          </Tooltip>
        ) : (
          <SvgWithBranding
            className={`${styles['download-icon']}`}
            type='download2'
            onClick={handleDownload}
          />
        )}
      </div>

      {series[0]?.data?.length === 0 ? (
        <NoData minHeight={470} />
      ) : (
        <ReactApexChart
          options={chartSettings.options}
          series={chartSettings.series}
          type='bar'
          height={dynamicHeight}
        />
      )}

      <Modal
        title='Application Status'
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        contentClassName={styles['modal']}
        classname={styles['modal-container']}
        children={
          <TableComponent
            rowKey='id'
            tableContainerClassName={styles['table-container']}
            headerData={{
              tableName: 'Application Status',
              hasExport: false,
              hasSearch: false,
              hasDelete: false,
              hasEdit: false,
              hasPaging: true,
              hasCheckbox: false,
              hasUploadCSV: false,
              hasFilter: false,
              hasExport: false,
              fields: [
                { fieldName: 'text', headerName: 'Question' },
                { fieldName: 'country', headerName: 'Country', hasSort: true },
                {
                  fieldName: 'dateCreated',
                  headerName: 'Date',
                  isDatetime: true,
                },
              ],
            }}
            externalData={questionsByWorkflow?.data || []}
          />
        }
      />
    </div>
  );
};

export default MostUsedWorkflow;
